//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import { BadgeType } from '@components/atoms/badge/Badge';
import { REQUEST_RIGHTS } from '@legacy-components/rights-management/request-rights-constants';
import { Entries } from '@utils/types/Entries';

import { Locales } from 'app/constants/locales/locale';
import { termsConditionsSettings } from 'app/constants/rights-management/terms-and-conditions-settings';

const RIGHT_REQUEST_TRACKING_PREFIX = 'automatic-rights';

export const RIGHT_REQUEST_TRACKING_IDS = {
  INSIGHTS_LINK: `${RIGHT_REQUEST_TRACKING_PREFIX}-insights-link`,
  RIGHT_BUTTON: `${RIGHT_REQUEST_TRACKING_PREFIX}-button`,
  TOGGLE_BUTTON: `${RIGHT_REQUEST_TRACKING_PREFIX}-toggle`,
  SAVE_BUTTON: `${RIGHT_REQUEST_TRACKING_PREFIX}-save`,
} as const;

export enum RightsRequestConfigurationStep {
  SetupRightsMessaging = 'SetupRightsMessaging',
  RightsOptionsMessages = 'RightsOptionsMessages',
  AddMessageForm = 'AddMessageForm',
  RightsOptionForm = 'RightsOptionForm',
  TermsAndConditions = 'TermsAndConditions',
}

export enum SelectedTermAndConditions {
  BazaarvoiceHosted = 'BazaarvoiceHosted',
  CustomLink = 'CustomLink',
}

export const TncTitles = {
  SetupTnc: 'Setup Terms and Conditions',
  EditTnc: 'Edit Terms and Conditions',
  ViewTnc: 'View Terms and Conditions',
} as const;

interface TncOptionConfig {
  Title: string;
  Content: string;
  SecondaryText: string;
  Badge?: {
    Text: string;
    Type: BadgeType;
  };
  Input?: {
    Label: string;
    HelpText: string;
  };
  ErrorMessages?: {
    EmptyUrl?: string;
    InvalidUrl?: string;
  };
}

export const REQUEST_RIGHTS_ALERT_INFO =
  'When you have more than one message in a campaign, Bazaarvoice will automatically rotate through the messages.';

export const TncOptionsTexts = {
  [SelectedTermAndConditions.BazaarvoiceHosted]: {
    Title: 'Bazaarvoice Hosted',
    Content:
      'Use the Bazaarvoice hosted terms & conditions page intended to cover a broad range of use cases. You should have your legal team review to ensure it covers your use cases.',
    SecondaryText: 'View your selected t&c page here:',
    Badge: {
      Text: 'Recommended',
      Type: BadgeType.Primary,
    },
  },
  [SelectedTermAndConditions.CustomLink]: {
    Title: 'Custom Link',
    Content:
      'Use your own branded terms & conditions page. You should have your legal team review to ensure it covers your use cases, including distribution.',
    Input: {
      Label: 'URL',
      HelpText: "View your selected T&C's page here",
    },
    SecondaryText: 'Preview Page:',
    ErrorMessages: {
      EmptyUrl: 'Please input a URL (http(s)://www.example.com)',
      InvalidUrl:
        'Please input a valid URL (http(s)://www.example.com, www.example.com)',
    },
  },
} as const satisfies Record<SelectedTermAndConditions, TncOptionConfig>;

export const BV_TERMS_AND_CONDITIONS_URL = 'https://bv-url.com/vc-tou';

export const BV_NETWORK_MESSAGES = [
  REQUEST_RIGHTS.BV_NETWORK_MSG_1,
  REQUEST_RIGHTS.BV_NETWORK_MSG_2,
  REQUEST_RIGHTS.BV_NETWORK_MSG_3,
  REQUEST_RIGHTS.BV_NETWORK_MSG_4,
  REQUEST_RIGHTS.BV_NETWORK_MSG_5,
] as const;

export const TIKTOK_NETWORK_MESSAGES = [
  REQUEST_RIGHTS.TIKTOK_NETWORK_MSG_1,
  REQUEST_RIGHTS.TIKTOK_NETWORK_MSG_2,
  REQUEST_RIGHTS.TIKTOK_NETWORK_MSG_3,
  REQUEST_RIGHTS.TIKTOK_NETWORK_MSG_4,
  REQUEST_RIGHTS.TIKTOK_NETWORK_MSG_5,
] as const;

export interface TermsConditionsOption {
  label: string;
  value: string;
  languageCode: Locales;
}

export interface RightsOptionFormState {
  campaignTitle: string;
  requestMessage: string;
  selectedTermsConditionsUrl?: string;
  languageCode: Locales;
  approvalPhrase: string;
  selectedIndex: number;
}

export interface RightsOptionErrorState {
  campaignTitle?: string;
  requestMessage?: string;
}

export interface RightsOptionsLoadingState {
  isConfigLoading: boolean;
  isTermsAndConditionLoading: boolean;
  isUpdatingRightsConfigurations: boolean;
  isUpdatingTermsAndCondition: boolean;
  isDeletingRightsConfiguration: boolean;
  isDetectingLanguage: boolean;
}

export const TERMS_CONDITIONS_OPTIONS: TermsConditionsOption[] = (
  Object.entries(termsConditionsSettings) as Entries<
    typeof termsConditionsSettings
  >
).map(([languageCode, settings]) => {
  return {
    label: `${settings.countryFlag} ${settings.label} ${settings.url}`,
    value: settings.url,
    languageCode,
  };
});

export const InitialRightsOptionFormState = {
  campaignTitle: '',
  requestMessage: '',
  selectedTermsConditionsUrl: termsConditionsSettings[Locales.EN_US]?.url,
  languageCode: Locales.EN_US,
  approvalPhrase: '',
  selectedIndex: -1,
} as RightsOptionFormState;

export const RIGHTS_OPTION_FORM_WIDTH = 440;

export const AUTOMATION_DEFAULT_MESSAGE_LIMIT = 4;

export interface RightsRequestLocaleOption {
  label: string;
  value: Locales;
}

export const RIGHTS_REQUEST_LOCALES: RightsRequestLocaleOption[] = [
  {
    label: 'Chinese',
    value: Locales.ZH_CN,
  },
  {
    label: 'Czech',
    value: Locales.CS_CZ,
  },
  {
    label: 'Danish',
    value: Locales.DA_DK,
  },
  {
    label: 'Dutch',
    value: Locales.NL_NL,
  },
  {
    label: 'English',
    value: Locales.EN_US,
  },
  {
    label: 'Finnish',
    value: Locales.FI_FI,
  },
  {
    label: 'French',
    value: Locales.FR_FR,
  },
  {
    label: 'German',
    value: Locales.DE_DE,
  },
  {
    label: 'Hungarian',
    value: Locales.HU_HU,
  },
  {
    label: 'Indonesian',
    value: Locales.ID_ID,
  },
  {
    label: 'Italian',
    value: Locales.IT_IT,
  },
  {
    label: 'Japanese',
    value: Locales.JA_JP,
  },
  {
    label: 'Korean',
    value: Locales.KO_KR,
  },
  {
    label: 'Polish',
    value: Locales.PL_PL,
  },
  {
    label: 'Portuguese',
    value: Locales.PT_BR,
  },
  {
    label: 'Romanian',
    value: Locales.RO_RO,
  },
  {
    label: 'Russian',
    value: Locales.RU_RU,
  },
  {
    label: 'Spanish',
    value: Locales.ES_ES,
  },
  {
    label: 'Swedish',
    value: Locales.SV_SE,
  },
  {
    label: 'Turkish',
    value: Locales.TR_TR,
  },
];

export const LANGUAGE_DETECTION_DEBOUNCE_TIME = 500;
export const LANGUAGE_DETECTION_MINIMUM_TEXT_LENGTH = 5;
export const DEFAULT_CONTENT_HEIGHT = '480px';
export const DEFAULT_SETUP_RIGHTS_MESSAGING_CONTENT_HEIGHT = '560px';
export const GENERAL_MESSAGING_TIKTOK = 'General Messaging TikTok';

interface TikTokLocaleStrings {
  retailerMessage: string;
  tagUsShort: string;
  tagUsSuffix: string;
}

export const TIKTOK_LOCALE_STRINGS: Partial<
  Record<Locales, TikTokLocaleStrings>
> = {
  [Locales.EN_US]: {
    retailerMessage: 'Learn more:',
    tagUsSuffix: 'tag us and reply with ',
    tagUsShort: 'tag us',
  },
  [Locales.DE_DE]: {
    retailerMessage: 'Mehr erfahren:',
    tagUsSuffix: 'markiere uns und antworte mit ',
    tagUsShort: 'markiere uns',
  },
  [Locales.FR_FR]: {
    retailerMessage: 'En savoir plus:',
    tagUsSuffix: 'identifiez-nous et répondez avec ',
    tagUsShort: 'identifiez-nous',
  },
  [Locales.ES_ES]: {
    retailerMessage: 'Más información:',
    tagUsSuffix: 'etiquétanos y responde con ',
    tagUsShort: 'etiquétanos',
  },
  [Locales.IT_IT]: {
    retailerMessage: 'Scopri di più:',
    tagUsSuffix: 'taggaci e rispondi con ',
    tagUsShort: 'taggaci',
  },
  [Locales.NL_NL]: {
    retailerMessage: 'Meer informatie:',
    tagUsSuffix: 'tag ons en antwoord met ',
    tagUsShort: 'tag ons',
  },
  [Locales.PT_BR]: {
    retailerMessage: 'Saiba mais:',
    tagUsSuffix: 'nos marque e responda com ',
    tagUsShort: 'nos marque',
  },
  [Locales.PL_PL]: {
    retailerMessage: 'Dowiedz się więcej:',
    tagUsSuffix: 'oznacz nas i odpowiedz z ',
    tagUsShort: 'oznacz nas',
  },
  [Locales.RU_RU]: {
    retailerMessage: 'Узнать больше:',
    tagUsSuffix: 'отметьте нас и ответьте с ',
    tagUsShort: 'отметьте нас',
  },
  [Locales.JA_JP]: {
    retailerMessage: '詳細を見る:',
    tagUsSuffix: 'タグ付けして返信してください ',
    tagUsShort: 'タグ付けして',
  },
  [Locales.KO_KR]: {
    retailerMessage: '자세히 알아보기:',
    tagUsSuffix: '저희를 태그하고 다음으로 답장해 주세요 ',
    tagUsShort: '저희를 태그하고',
  },
  [Locales.ZH_CN]: {
    retailerMessage: '了解更多:',
    tagUsSuffix: '标记我们并回复 ',
    tagUsShort: '标记我们',
  },
  [Locales.SV_SE]: {
    retailerMessage: 'Läs mer:',
    tagUsSuffix: 'tagga oss och svara med ',
    tagUsShort: 'tagga oss',
  },
  [Locales.DA_DK]: {
    retailerMessage: 'Lær mere:',
    tagUsSuffix: 'tag os og svar med ',
    tagUsShort: 'tag os',
  },
  [Locales.FI_FI]: {
    retailerMessage: 'Lue lisää:',
    tagUsSuffix: 'merkitse meidät ja vastaa ',
    tagUsShort: 'merkitse meidät',
  },
  [Locales.CS_CZ]: {
    retailerMessage: 'Zjistěte více:',
    tagUsSuffix: 'označte nás a odpovězte s ',
    tagUsShort: 'označte nás',
  },
  [Locales.HU_HU]: {
    retailerMessage: 'Tudjon meg többet:',
    tagUsSuffix: 'jelöljön meg minket és válaszoljon ezzel: ',
    tagUsShort: 'jelöljön meg minket',
  },
  [Locales.ID_ID]: {
    retailerMessage: 'Pelajari lebih lanjut:',
    tagUsSuffix: 'tandai kami dan balas dengan ',
    tagUsShort: 'tandai kami',
  },
  [Locales.TR_TR]: {
    retailerMessage: 'Daha fazla bilgi:',
    tagUsSuffix: 'bizi etiketleyin ve şununla yanıtlayın: ',
    tagUsShort: 'bizi etiketleyin',
  },
  [Locales.RO_RO]: {
    retailerMessage: 'Aflați mai multe:',
    tagUsSuffix: 'etichetați-ne și răspundeți cu ',
    tagUsShort: 'etichetați-ne',
  },
};

const EN_TIKTOK_FALLBACK = TIKTOK_LOCALE_STRINGS[Locales.EN_US];

// Full suffix phrases must come before short phrases so stripping removes the full
// phrase first (preventing the short phrase from matching within the full one).
export const TIKTOK_TAG_US_PHRASES: string[] = [
  ...Object.values(TIKTOK_LOCALE_STRINGS).map((locale) =>
    locale.tagUsSuffix.trim(),
  ),
  ...Object.values(TIKTOK_LOCALE_STRINGS).map((locale) =>
    locale.tagUsShort.trim(),
  ),
];

export const TIKTOK_TAG_US_SHORT_PHRASES: string[] = Object.values(
  TIKTOK_LOCALE_STRINGS,
).map((locale) => locale.tagUsShort.trim());

export const getTikTokTagUsSuffix = (languageCode: Locales): string =>
  TIKTOK_LOCALE_STRINGS[languageCode]?.tagUsSuffix ??
  EN_TIKTOK_FALLBACK?.tagUsSuffix ??
  'tag us and reply with ';

export const getTikTokTagUsShort = (languageCode: Locales): string =>
  TIKTOK_LOCALE_STRINGS[languageCode]?.tagUsShort ??
  EN_TIKTOK_FALLBACK?.tagUsShort ??
  'tag us';

export const getTikTokRetailerMessage = (languageCode: Locales): string =>
  TIKTOK_LOCALE_STRINGS[languageCode]?.retailerMessage ??
  EN_TIKTOK_FALLBACK?.retailerMessage ??
  'Learn more:';
